import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

export default function Tools() {
  const [tools, setTools] = useState([]);
  const [filter, setFilter] = useState("All");
  const [quoteTool, setQuoteTool] = useState(null);

  useEffect(() => {
    api.get("/tools").then((r) => setTools(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(tools.map((t) => t.category)))], [tools]);
  const filtered = filter === "All" ? tools : tools.filter((t) => t.category === filter);

  return (
    <div>
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-20">
          <span className="font-mono-label text-[11px] text-zinc-400">Software store</span>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-black tracking-tighter text-zinc-900 sm:text-5xl lg:text-6xl">
            Licensed software & tools
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-600">
            Genuine licenses for the platforms your business runs on — from Microsoft Office Plus to Exchange and Windows Server. Request a quote and we'll get you set up.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-16">
        <div className="mb-8 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`tools-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`border px-4 py-2 text-xs font-semibold transition-colors ${
                filter === c ? "border-klein bg-klein text-white" : "border-zinc-200 text-zinc-600 hover:border-zinc-400"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-px border-l border-t border-zinc-200 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={`tool-card-${t.id}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
              className="group flex flex-col border-b border-r border-zinc-200 bg-white"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-zinc-100">
                <img src={t.image} alt={t.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                {t.badge && (
                  <span className="absolute left-0 top-0 bg-accentred px-2.5 py-1 font-mono-label text-[10px] text-white">{t.badge}</span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="font-mono-label text-[10px] text-zinc-400">{t.category}</span>
                <h3 className="mt-2 font-heading text-lg font-semibold leading-tight text-zinc-900">{t.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-500">{t.description}</p>
                <div className="mt-5 flex items-center justify-between">
                  <span className="font-heading text-xl font-bold text-klein">{t.price || "Contact"}</span>
                  <button
                    onClick={() => setQuoteTool(t)}
                    data-testid={`request-quote-${t.id}`}
                    className="inline-flex items-center gap-1.5 bg-klein px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-kleindark"
                  >
                    <ShoppingCart className="h-3.5 w-3.5" /> Request quote
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {quoteTool && <QuoteModal tool={quoteTool} onClose={() => setQuoteTool(null)} />}
    </div>
  );
}

function QuoteModal({ tool, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", qty: "1" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/leads", {
        ...form,
        summary: `Quote request for "${tool.name}" (${tool.category}) — listed price ${tool.price || "N/A"}. Quantity: ${form.qty}.`,
        source: "tools-quote",
      });
      setDone(true);
    } catch (e) {
      setError("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md border border-zinc-200 bg-white" onClick={(e) => e.stopPropagation()} data-testid="quote-modal">
        <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-4">
          <span className="font-mono-label text-[11px] text-zinc-500">Request a quote</span>
          <button onClick={onClose} data-testid="quote-close"><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>

        {done ? (
          <div className="flex flex-col items-center px-6 py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-klein" strokeWidth={1.3} />
            <h3 className="mt-4 font-heading text-xl font-bold text-zinc-900">Quote requested!</h3>
            <p className="mt-2 text-sm text-zinc-600">Our team will email you a quote for {tool.name} shortly.</p>
            <button onClick={onClose} className="mt-6 bg-klein px-6 py-2.5 text-sm font-semibold text-white hover:bg-kleindark">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 p-6">
            <div className="border border-zinc-200 bg-zinc-50 p-3">
              <p className="font-heading text-sm font-semibold text-zinc-900">{tool.name}</p>
              <p className="text-xs text-zinc-500">{tool.price || "Custom pricing"}</p>
            </div>
            <input data-testid="quote-name" required placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
            <input data-testid="quote-email" required type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="quote-company" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
              <input data-testid="quote-qty" type="number" min="1" placeholder="Qty" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
            </div>
            <input data-testid="quote-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-zinc-200 px-3 py-2.5 text-sm outline-none focus:border-klein" />
            {error && <p className="text-sm text-accentred">{error}</p>}
            <button type="submit" disabled={submitting} data-testid="quote-submit" className="flex w-full items-center justify-center gap-2 bg-klein px-6 py-3 text-sm font-semibold text-white hover:bg-kleindark disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send request
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
